import { defineMutation, useMutation, useQueryCache } from '@pinia/colada';

import { useAlertsStore } from '@tallo/design-system';
import {
  getCompanyWantsToApplyQueryKey,
  getPropertyInquiriesQueryKey,
  PropertyInquiryDto,
  propertyInquiryService,
} from '@tallo/investor/property';
import { escapeHtml } from '@tallo/utility';

import { renterConversationQueryKey } from '../quieries/renter-conversation.query';
import { renterInquiriesQueryKey } from '../quieries/renter-inquiries.query';

const firstNameOf = (inquiry: PropertyInquiryDto) => escapeHtml(inquiry.renter.user.name.split(' ')[0]);

/**
 * Every answer takes the renter off every Wants to apply list: the unit's sits under its inquiries key (which also
 * carries the "Wants to apply" mark on the leads list), Home's has its own, and the renter profile marks its cards too.
 */
function invalidateWantsToApply(propertyId: string) {
  const queryCache = useQueryCache();

  queryCache.invalidateQueries({ key: [renterInquiriesQueryKey] });
  queryCache.invalidateQueries({ key: getCompanyWantsToApplyQueryKey() });

  return queryCache.invalidateQueries({ key: getPropertyInquiriesQueryKey(propertyId) });
}

export const useIgnoreWantsToApply = defineMutation(() => {
  return useMutation({
    mutation(inquiry: PropertyInquiryDto) {
      return propertyInquiryService.ignoreWantsToApply(inquiry.id);
    },

    async onSuccess(_, inquiry) {
      await invalidateWantsToApply(inquiry.property.id);

      useAlertsStore().success(`${firstNameOf(inquiry)}'s request is hidden`);
    },

    onError(error) {
      useAlertsStore().catchError(error);
    },
  });
});

export const useAnswerWantsToApply = defineMutation(() => {
  return useMutation({
    mutation(payload: { inquiry: PropertyInquiryDto; message: string }) {
      return propertyInquiryService.answerWantsToApply(payload.inquiry.id, payload.message);
    },

    async onSuccess(_, { inquiry }) {
      // The note lands in the renter's thread, so a transcript already on screen is stale
      useQueryCache().invalidateQueries({ key: [renterConversationQueryKey] });
      await invalidateWantsToApply(inquiry.property.id);

      useAlertsStore().success(`Sent to ${firstNameOf(inquiry)}`);
    },

    // The server's words say why nothing went out: the screen's reason, a paused or closed conversation
    onError(error) {
      useAlertsStore().catchError(error);
    },
  });
});

export const useAskToTourFirst = defineMutation(() => {
  return useMutation({
    mutation(inquiry: PropertyInquiryDto) {
      return propertyInquiryService.askToTourFirst(inquiry.id);
    },

    async onSuccess(_, inquiry) {
      // The note lands in the renter's thread, so a transcript already on screen is stale
      useQueryCache().invalidateQueries({ key: [renterConversationQueryKey] });
      await invalidateWantsToApply(inquiry.property.id);

      useAlertsStore().success(`Asked ${firstNameOf(inquiry)} to tour first`);
    },

    // The server's words say why nothing went out: already toured, already answered, a paused or closed conversation
    onError(error) {
      useAlertsStore().catchError(error);
    },
  });
});
